import type {
  HeatmapCell,
  HeatmapColumnKind,
  PreflightMarketKind,
  ProjectedFeature,
} from "@nexus/contracts";
import type {
  PreflightReportAsset,
  PreflightReportDetail,
  PreflightScoreBand,
} from "../types/preflight.types";
import { creativeDisplayLabel } from "./format-asset-name";

/**
 * What one heatmap column stands for: a creative on report detail, or a market /
 * report in the market aggregate (R22). The two aggregate cases mirror
 * `HeatmapColumnKind` so a server response needs no translation.
 */
export type MatrixColumnKind = "CREATIVE" | HeatmapColumnKind;

/** One scored creative as a score-rail dot. */
export interface MatrixAsset {
  id: string;
  label: string;
  score: number | null;
  band: PreflightScoreBand | null;
  /** Preview thumbnail (thumbnail URL, falling back to the signed asset URL). */
  thumbnailUrl?: string | null;
  /** Signed media URL — used to autoplay video in large hover previews. */
  assetUrl?: string | null;
  /** Whether the asset is a video — drives the fallback glyph when no thumbnail exists. */
  isVideo?: boolean;
}

/**
 * One heatmap column. Report detail supplies a creative (`score`, `band`, preview);
 * the aggregate supplies a market or report (`code`, `avgScore`, `creativeCount`), so
 * `HeatmapColumn` is directly assignable.
 */
export interface MatrixColumn {
  id: string;
  label: string;
  /** Compact header text — a market code; null for report and creative columns. */
  code?: string | null;
  /** Catalogue kind for a market column — picks the flag, globe or region glyph. */
  kind?: PreflightMarketKind | null;
  score?: number | null;
  band?: PreflightScoreBand | null;
  /** Mean score across the column's creatives (aggregate columns). */
  avgScore?: number | null;
  /** Creatives behind an aggregate column. */
  creativeCount?: number;
  /** Reports behind a market column's average; 1 for report columns. */
  reportCount?: number;
  thumbnailUrl?: string | null;
  /** Signed media URL — used to autoplay video in large hover previews. */
  assetUrl?: string | null;
  isVideo?: boolean;
}

/**
 * One column × criterion intersection. Report detail knows the creative's single
 * verdict (null when the creative lacks the criterion); the aggregate knows only the
 * counts behind the column, so a cell carries whichever its producer has.
 */
export interface MatrixCell extends Partial<HeatmapCell> {
  feature?: ProjectedFeature | null;
}

/** One criterion row across every column; assignable from the aggregate's `HeatmapRow`. */
export interface MatrixRow {
  key: string;
  name: string;
  /** Catalogue definition for the criterion-name hover tooltip; null when copy is mixed. */
  definition: string | null;
  /** Representative (max observed) DS weight. */
  weight: number;
  cells: MatrixCell[];
  failures: number;
  partials: number;
  passes: number;
}

/** Criteria × columns grid; assignable from the aggregate's `InsightsHeatmap`. */
export interface CriteriaMatrix {
  columnKind: MatrixColumnKind;
  columns: MatrixColumn[];
  rows: MatrixRow[];
}

function isScored(asset: PreflightReportAsset): boolean {
  return asset.status === "SCORED" && asset.score != null;
}

/**
 * Scored creatives as lightweight column/dot descriptors. Unnamed creatives are numbered by
 * their position in the whole report — not in the scored subset — so "Creative 3" means the
 * same creative in the rail, the heatmap and the card list.
 */
export function scoredMatrixAssets(
  report: PreflightReportDetail,
  fallbackLabel: string,
): MatrixAsset[] {
  return report.assets.flatMap((asset, index) => {
    if (!isScored(asset)) return [];
    const isVideo = asset.contentType?.startsWith("video/") ?? false;
    return {
      id: asset.id,
      label: creativeDisplayLabel(asset.originalName, index + 1, fallbackLabel),
      score: asset.score,
      band: asset.band,
      // A video's own URL can't stand in for a missing thumbnail: it is not an image source,
      // so it would paint an empty frame instead of the media-type glyph.
      thumbnailUrl: asset.thumbnailUrl ?? (isVideo ? null : asset.assetUrl),
      assetUrl: asset.assetUrl,
      isVideo,
    };
  });
}

function featureKey(feature: ProjectedFeature): string {
  return feature.code ?? feature.name;
}

function isContributing(feature: ProjectedFeature): boolean {
  return (
    feature.state === "PASS" ||
    feature.state === "FAIL" ||
    feature.state === "PARTIAL"
  );
}

/** Row tooltips only when every scored cell agrees — mixed image/video copy stays on cells. */
function rowDefinitionFromFeatures(
  features: Iterable<ProjectedFeature>,
): string | null {
  let chosen: string | null = null;
  for (const feature of features) {
    if (!feature.definition) continue;
    if (chosen === null) {
      chosen = feature.definition;
      continue;
    }
    if (chosen !== feature.definition) return null;
  }
  return chosen;
}

interface RowAccumulator {
  key: string;
  name: string;
  weight: number;
  displayOrder: number;
  byAsset: Map<string, ProjectedFeature>;
}

/**
 * Pivots a report's scored creatives into a criteria × creatives matrix powering the
 * heatmap. Rows are limited to criteria that produced a real verdict somewhere (the
 * three excluded states alone never earn a row) and ranked so systemic weaknesses —
 * the criteria failing across the most creatives — surface at the top.
 */
export function buildCriteriaMatrix(
  report: PreflightReportDetail,
  fallbackLabel: string,
): CriteriaMatrix {
  const scored = report.assets.filter(isScored);
  const assets: MatrixColumn[] = scoredMatrixAssets(report, fallbackLabel);

  const accumulators = new Map<string, RowAccumulator>();
  for (const asset of scored) {
    for (const feature of asset.features) {
      const key = featureKey(feature);
      const entry =
        accumulators.get(key) ??
        ({
          key,
          name: feature.name,
          weight: feature.weight,
          displayOrder: feature.displayOrder ?? Number.MAX_SAFE_INTEGER,
          byAsset: new Map<string, ProjectedFeature>(),
        } satisfies RowAccumulator);
      entry.weight = Math.max(entry.weight, feature.weight);
      if (feature.displayOrder != null) {
        entry.displayOrder = Math.min(entry.displayOrder, feature.displayOrder);
      }
      entry.byAsset.set(asset.id, feature);
      accumulators.set(key, entry);
    }
  }

  const rows: MatrixRow[] = [];
  for (const acc of accumulators.values()) {
    const cells: MatrixCell[] = scored.map((asset) => ({
      feature: acc.byAsset.get(asset.id) ?? null,
    }));

    let failures = 0;
    let partials = 0;
    let passes = 0;
    let contributes = false;
    for (const cell of cells) {
      if (!cell.feature) continue;
      if (!isContributing(cell.feature)) continue;
      contributes = true;
      if (cell.feature.state === "FAIL") failures += 1;
      else if (cell.feature.state === "PARTIAL") partials += 1;
      else passes += 1;
    }
    if (!contributes) continue;

    rows.push({
      key: acc.key,
      name: acc.name,
      definition: rowDefinitionFromFeatures(acc.byAsset.values()),
      weight: acc.weight,
      cells,
      failures,
      partials,
      passes,
    });
  }

  rows.sort((a, b) => {
    const aMiss = a.failures + a.partials * 0.5;
    const bMiss = b.failures + b.partials * 0.5;
    return bMiss - aMiss || b.weight - a.weight || a.name.localeCompare(b.name);
  });

  return { columnKind: "CREATIVE", columns: assets, rows };
}
